import React, {useCallback, useEffect, useState} from 'react';
import {MerchantContextType, SecurityContextType} from './App.model';
import NxLoader from './components/shared/nxLoader/NxLoader';
import RenderRoutes from './routes/render-routes/RenderRoutes';
import {ROUTES} from './routes/routes';
import {MERCHANTS_URL, SESSION_DATA_URL} from './shared/constants/api-urls';
import useGet from './shared/hooks/use-get.hook';
import {Merchant} from './shared/model/Merchant.model';
import {UserData} from './shared/model/UserData.model';
import './styles/styles.scss';
import {isTokenValid, removeSessionToken} from './utils/security';
import {EnvironmentType} from './shared/model/EnvironmentType.model';
import useSessionExpire from './shared/hooks/use-session-expire';
import {useTranslation} from 'react-i18next';
import {useHistory} from 'react-router';
import {RoutesPaths} from './routes/routes.paths';

export const SecurityContext = React.createContext<SecurityContextType>({
  isLogged: undefined,
  setIsLogged: () => null,
  logoutReason: undefined,
  setLogoutReason: () => null,
  setUserData: () => null,
  startTimeoutTimer: () => null
});

export const MerchantContext = React.createContext<MerchantContextType>({
  setMerchants: () => null,
  setCurrentMerchant: () => null,
  env: EnvironmentType.PRODUCTION,
  setEnv: () => null
});

function App(): React.ReactElement {

  const {t} = useTranslation();
  const history = useHistory();
  const [isLogged, setIsLogged] = useState<boolean>();
  const [userData, setUserData] = useState<UserData>();
  const [logoutReason, setLogoutReason] = useState<string>();
  const user = useGet<UserData>(SESSION_DATA_URL);
  const merchantsRequest = useGet<Array<Merchant>>(MERCHANTS_URL);
  const [merchants, setMerchants] = useState<Merchant[]>();
  const [currentMerchant, setCurrentMerchant] = useState<Merchant>();
  const [env, setEnv] = useState<EnvironmentType>(EnvironmentType.PRODUCTION);

  const onExpire = (): void => {
    removeSessionToken();
    setIsLogged(false);
    setLogoutReason(t('LOGIN.SESSION_EXPIRED_ERROR'));

    history.replace(RoutesPaths.LOGIN);
  };

  const startTimeoutTimer = useSessionExpire(onExpire);

  const fetchUserData = useCallback(async () => {
    // fetch session data
    const userData = await user().catch(() => {
      setIsLogged(false);
      return null;
    });

    if (!userData) {
      setIsLogged(false);
      return null;
    }

    setUserData(userData);
    setIsLogged(true);
    startTimeoutTimer();

    // fetch merchants
    const merchants = await merchantsRequest().catch(() => []);

    setMerchants(merchants);
    if (merchants.length) {
      setCurrentMerchant(merchants[0]);
    }
  }, []);

  useEffect(() => {
    if (isTokenValid()) {
      fetchUserData();
      return;
    }
    setIsLogged(false);
  }, []);

  return (
    <SecurityContext.Provider value={{
      isLogged,
      setIsLogged,
      logoutReason,
      setLogoutReason,
      userData,
      setUserData,
      startTimeoutTimer
    }}>
      <MerchantContext.Provider value={{
        merchants,
        setMerchants,
        currentMerchant,
        currentMerchantId: currentMerchant?.id,
        setCurrentMerchant,
        env,
        setEnv
      }}>
        <NxLoader loaded={isLogged !== undefined}>
          <RenderRoutes
            routes={ROUTES}
            isLogged={isLogged}
            isEnabledDisbursement={currentMerchant?.enableDisbursements || env === EnvironmentType.SANDBOX} />
        </NxLoader>
      </MerchantContext.Provider>
    </SecurityContext.Provider>
  );
}

export default App;
